import { useEffect, useState } from 'react';
import { Download, Smartphone, ChevronRight } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWAInstallProps {
  variant?: 'floating' | 'inline' | 'login';
  className?: string;
}

export default function PWAInstall({ variant = 'floating', className = '' }: PWAInstallProps) {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Verificar se já está instalado
    const checkInstalled = () => {
      // Verificar se está rodando em modo standalone (instalado)
      const standalone = window.matchMedia('(display-mode: standalone)').matches;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const isStandaloneIOS = (window.navigator as any).standalone === true;
      
      // Verificar se é iOS
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
      setIsIOS(iOS);
      
      // Se já está instalado, não mostrar o botão
      if (standalone || isStandaloneIOS) {
        setIsInstalled(true);
        return;
      }
    };

    checkInstalled();

    // Handler para o evento beforeinstallprompt (Chrome/Edge)
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handler);
    
    // Verificar mudanças no modo de exibição
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => {
      checkInstalled();
    };
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  const onInstall = async () => {
    if (!deferredPrompt) return;
    
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    } catch (error) {
      console.error('Erro ao instalar:', error);
    }
  };

  // Se já está instalado, não mostrar nada
  if (isInstalled) {
    return null;
  }

  // Se é iOS e não tem prompt disponível, mostrar instruções
  if (isIOS && !deferredPrompt) {
    if (variant === 'inline') {
      return (
        <div className={`rounded-[18px] p-6 bg-[#141414] border shadow-lg ${className}`} style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-[#E53935]/10 border border-[#E53935]/20">
              <Smartphone className="w-5 h-5 text-[#E53935]" />
            </div>
            <h3 className="text-white font-bold text-base">Instalar App</h3>
          </div>
          <div className="space-y-3">
            <p className="text-[#A1A1A1] text-sm leading-relaxed">
              Para instalar o ShapeX no seu iPhone:
            </p>
            <ol className="list-decimal list-inside space-y-2 text-[#A1A1A1] text-sm">
              <li>Toque no botão de compartilhar <span className="text-white">□↑</span></li>
              <li>Selecione <span className="text-white">"Adicionar à Tela de Início"</span></li>
              <li>Toque em <span className="text-white">"Adicionar"</span></li>
            </ol>
          </div>
        </div>
      );
    }
    // Para iOS na tela de login, mostrar o botão mesmo sem prompt
    if (variant === 'login') {
      return (
        <button
          onClick={() => {
            // Para iOS, não há prompt, então apenas mostra uma mensagem ou não faz nada
            // O usuário precisa seguir as instruções do Safari
          }}
          className={`px-3 py-2 rounded-lg text-xs transition-all duration-200 hover:opacity-90 active:scale-95 flex items-center gap-1.5 ${className}`}
          style={{
            backgroundColor: '#E53935',
            color: 'white',
          }}
        >
          <Smartphone className="w-3.5 h-3.5 text-white" strokeWidth={2} />
          <span>baixe o app aqui!</span>
        </button>
      );
    }
    return null;
  }

  // Botão para tela de login (minimalista) - pequeno e no canto
  if (variant === 'login') {
    return (
      <button
        onClick={onInstall}
        className={`px-3 py-2 rounded-lg text-xs transition-all duration-200 hover:opacity-90 active:scale-95 flex items-center gap-1.5 ${className}`}
        style={{
          backgroundColor: '#E53935',
          color: 'white',
        }}
      >
        <Smartphone className="w-3.5 h-3.5 text-white" strokeWidth={2} />
        <span>baixe o app aqui!</span>
      </button>
    );
  }

  // Se não tem prompt disponível (Android já instalado ou não suportado), não mostrar para outras variantes
  if (!deferredPrompt) {
    return null;
  }

  // Botão flutuante (padrão)
  if (variant === 'floating') {
    return (
      <button
        onClick={onInstall}
        className="fixed right-4 bottom-24 z-50 bg-[#141414] text-white border rounded-xl px-4 py-3 shadow-2xl backdrop-blur-sm transition-all duration-200 hover:scale-105 active:scale-95 flex items-center gap-2"
        style={{
          borderColor: 'rgba(255, 255, 255, 0.1)',
          boxShadow: '0 6px 20px rgba(0,0,0,0.5)'
        }}
      >
        <Download className="w-4 h-4" />
        <span className="text-sm font-bold">Instalar</span>
      </button>
    );
  }

  // Botão inline (para página de perfil)
  return (
    <button
      onClick={onInstall}
      className={`w-full p-4 rounded-xl border transition-all duration-200 hover:scale-[1.02] text-left group flex items-center justify-between ${className}`}
      style={{
        backgroundColor: 'rgba(229, 57, 53, 0.05)',
        borderColor: 'rgba(229, 57, 53, 0.2)',
      }}
    >
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-[#E53935]/10 text-[#E53935]">
          <Download className="w-5 h-5" />
        </div>
        <div className="text-left">
          <span className="text-white font-bold text-sm block group-hover:text-[#E53935] transition-colors">
            Instalar ShapeX
          </span>
          <span className="text-[#A1A1A1] text-xs block mt-0.5">
            Instale o app na sua tela inicial
          </span>
        </div>
      </div>
      <ChevronRight className="w-5 h-5 text-[#A1A1A1] group-hover:text-[#E53935] transition-colors" />
    </button>
  );
}